import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTrimContext } from '../../../context/VideoTrimContex';
import Slider from 'rc-slider';
import styles from './VideoTrimmingControls.module.scss';
import TimeInput from '../../../components/TimeInput/TimeInput';
import { Icon } from '../../../components/Icon';
import controlBgImage from './controlBgImage.svg';
import position from './position.svg';
import { useHistory } from 'react-router-dom';
import Waveform from '../../../components/Waveform/Waveform';
import FloatingContainer from './FloatingContainer';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../../common/components/Spinner';
import cx from 'classnames';
import * as Sentry from '@sentry/browser';

const MIN_HEIGHT_COEF = 0.75;
const NORMAN_HEIGHT = 56;
const RAIL_NORMAN_HEIGHT = 58;
const HANDLER_NORMAN_HEIGHT = 65;
const HANDLER_NORMAN_TOP = 2;
const MINIMUM_TRIMMING_DURATION = 0.1; // 200 ms
const TRIMMING_STEP = 0.05;

const VideoTrimmingControls = ({ trimStart, trimEnd, waveform = null, className, min }) => {
  const {
    trimmingOn,
    startTrimStamp,
    endTrimStamp,
    updateStartTrimStamp,
    updateEndTrimStamp,
    currentProgress,
    trimmingVideoDuration,
    trimmingRevert,
    toggleTrimmingRevert,
    trimmingActionsBuffer,
    currentActionIndex,
    addNewAction,
    updateEditor,
    toggleUpdateEditor,
    updateTrimmingProgress,
    clearAfterTrimmingDone,
    setControlInUse,
  } = useTrimContext();

  const [startTimeInput, setStartTimeInput] = useState(trimStart / 1000);
  const [endTimeInput, setEndTimeInput] = useState(trimEnd / 1000);

  const [actionFinished, setActionFinished] = useState(false);
  const [finishStatus, setfinishStatus] = useState(false);

  const [showSeekTo, setShowSeekTo] = useState(false);
  const [pointerPosition, setPointerPosition] = useState(0);
  const [isRangeChanging, setIsRangeChanging] = useState(false);
  const [chosenHandler, setChosenHandler] = useState(-1);
  const [handlersPositions, setHandlersPositions] = useState([
    startTimeInput / trimmingVideoDuration,
    endTimeInput / trimmingVideoDuration,
  ]);
  const [init, setInit] = useState(true);

  const history = useHistory();
  const { t } = useTranslation();

  const onBackButtonEvent = e => {
    e.preventDefault();
    if (!finishStatus) {
      if (window.confirm('Changes that you made may not be saved.')) {
        setfinishStatus(true);
        history.goBack();
      } else {
        setfinishStatus(false);
      }
    }
  };

  const trimContainerRef = useRef(null);

  const onReloadEvent = event => {
    const e = event || window.event;
    // Cancel the event
    e.preventDefault();
    if (e) {
      e.returnValue = ''; // Legacy method for cross browser support
    }
    return ''; // Legacy method for cross browser support
  };

  useEffect(() => {
    window.addEventListener('popstate', onBackButtonEvent);
    window.addEventListener('beforeunload', onReloadEvent);
    return async () => {
      window.removeEventListener('beforeunload', onReloadEvent);
      await setStartTimeInput(trimStart / 1000);
      await setEndTimeInput(trimEnd / 1000);
      console.log('trimming did unmount');
      await updateStartTrimStamp(trimStart);
      await updateEndTrimStamp(trimEnd);
      trimEnd > 0 && trimStart === trimEnd && Sentry.captureMessage(`VideoTrimmingControls didUnmount trimStart === trimEnd event, ${trimStart} === ${trimEnd}`);
      await clearAfterTrimmingDone();
      window.removeEventListener('popstate', onBackButtonEvent);
    };
  }, []);

  // useEffect to add action to buffer after it has been finished
  useEffect(() => {
    if (actionFinished) {
      isRangeChanging &&
        addNewAction({
          startTrim: startTimeInput,
          endTrim: endTimeInput,
        });
      setIsRangeChanging(false);
      setActionFinished(false);
    }
  }, [actionFinished]);

  useEffect(() => {
    setControlInUse(isRangeChanging);
  }, [isRangeChanging]);

  // After editor was update ()
  useEffect(() => {
    if (updateEditor) {
      const action = trimmingActionsBuffer[currentActionIndex];
      if (action) {
        setStartTimeInput(action.startTrim);
        setEndTimeInput(action.endTrim);
        action.endTrim > 0 && action.startTrim === action.endTrim && Sentry.captureMessage(`VideoTrimmingControls undo/redo action: ${action}, startTrim=${action.startTrim} === endTrim=${action.endTrim}`);
      }
      toggleUpdateEditor(false);
    }
  }, [updateEditor]);

  const updateTrimmingValues = useCallback(
    e => {
      init && setInit(false);
      if ((e[1] - e[0]) * trimmingVideoDuration > MINIMUM_TRIMMING_DURATION) {
        setStartTimeInput(e[0] * trimmingVideoDuration);
        setEndTimeInput(e[1] * trimmingVideoDuration);
        if (
          Math.abs(e[0] * trimmingVideoDuration - startTimeInput) > TRIMMING_STEP ||
          Math.abs(e[1] * trimmingVideoDuration - endTimeInput) > TRIMMING_STEP
        ) {
          setIsRangeChanging(true);
        }
      }
    },
    [trimmingVideoDuration]
  );

  useEffect(() => {
    setHandlersPositions([startTimeInput / trimmingVideoDuration, endTimeInput / trimmingVideoDuration]);
  }, [startTimeInput, endTimeInput, trimmingVideoDuration]);

  useEffect(() => {
    updateStartTrimStamp(trimStart);
    updateEndTrimStamp(trimEnd);
    trimEnd > 0 && trimStart === trimEnd && Sentry.captureMessage(`VideoTrimmingControls updated trimmingVideoDuration ${trimmingVideoDuration} startTrim=${trimStart} === endTrim=${trimEnd}`);
    setStartTimeInput(trimStart / 1000);
    setEndTimeInput(trimEnd / 1000);
    if (trimmingActionsBuffer.length === 0) {
      addNewAction({
        startTrim: trimStart / 1000,
        endTrim: trimEnd / 1000,
      });
    }
  }, [trimmingVideoDuration]);

  useEffect(() => {
    updateStartTrimStamp(startTimeInput * 1000);
    endTrimStamp > 0 && startTimeInput * 1000 === endTrimStamp && Sentry.captureMessage(`VideoTrimmingControls updated startTrimStamp ${startTimeInput} startTrimStamp === endTrimStamp`);
    setChosenHandler(0);
  }, [startTimeInput]);

  useEffect(() => {
    updateEndTrimStamp(endTimeInput * 1000);
    endTimeInput * 1000 > 0 && endTimeInput * 1000 === startTrimStamp && Sentry.captureMessage(`VideoTrimmingControls updated endTimeInput ${endTimeInput} startTrimStamp === endTrimStamp`);
    !init && setChosenHandler(1);
  }, [endTimeInput]);

  useEffect(() => {
    console.log('chosenHandler', chosenHandler);
  }, [chosenHandler]);

  useEffect(() => {
    if (trimmingRevert) {
      console.log('trimmingRevert', trimmingRevert);
      setStartTimeInput(0);
      setEndTimeInput(trimmingVideoDuration);
      addNewAction({ startTrim: 0, endTrim: trimmingVideoDuration });
      setHandlersPositions([0, 1]);
      toggleTrimmingRevert(false);
    }
  }, [trimmingRevert]);

  useEffect(() => {
    if (trimmingOn) {
      setStartTimeInput(trimStart / 1000);
      setEndTimeInput(trimEnd / 1000);
    } else {
      window.removeEventListener('popstate', onBackButtonEvent);
      window.removeEventListener('beforeunload', onReloadEvent);
    }
  }, [trimmingOn]);

  useEffect(() => {
    console.log(
      'component did mount',
      { startTimeInput, endTimeInput, trimmingVideoDuration },
      startTimeInput / trimmingVideoDuration,
      endTimeInput / trimmingVideoDuration
    );
    console.log(
      'trimStart / trimmingVideoDuration, trimEnd / trimmingVideoDuration',
      trimStart / trimmingVideoDuration,
      trimEnd / trimmingVideoDuration
    );
  }, []);

  const onSeekMouseDown = useCallback(() => {
    console.log('on Mouse Down');
  }, []);

  const onSeekChange = useCallback(
    value => {
      if (value >= startTrimStamp / (trimmingVideoDuration * 1000) && value <= endTrimStamp / (trimmingVideoDuration * 1000)) {
        console.log('updateTrimmingProgress', { value, startTrimStamp, endTrimStamp, trimmingVideoDuration });
        updateTrimmingProgress(value);
      }
    },
    [startTrimStamp, endTrimStamp, trimmingVideoDuration, updateTrimmingProgress]
  );

  const onSeekMouseUp = useCallback(value => {
    console.log('on Mouse Up');
  }, []);

  const handleOnMouseMove = e => {
    const cursorPosition = e.clientX - trimContainerRef.current.offsetLeft - trimContainerRef.current.offsetParent.offsetLeft;
    setPointerPosition(cursorPosition - 6);
  };

  const handleMouseDown = e => {
    const tabIndex = e.target.tabIndex;
    if (tabIndex >= 0) {
      setChosenHandler(tabIndex);
    }
  };

  useEffect(() => {
    const body = document.querySelector('body');
    body.addEventListener('mousedown', handleMouseDown);

    return () => {
      body.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return (
    <div className={cx(styles.trimInstruments, className)} ref={trimContainerRef}>
      {showSeekTo && !isRangeChanging && (
        <div className={cx(styles.cursor, { [styles.min]: min })} style={{ left: `${pointerPosition}px` }}></div>
      )}
      <div
        className={cx(styles.slidersWrapper, { [styles.min]: min })}
        onMouseMove={handleOnMouseMove}
        onMouseOver={() => setShowSeekTo(true)}
        onMouseLeave={() => setShowSeekTo(false)}
      >
        {chosenHandler >= 0 && (
          <FloatingContainer
            className={styles.dragIconContainer}
            parentRef={trimContainerRef}
            floatingValue={handlersPositions[chosenHandler]}
            correction={init ? -15 : chosenHandler ? -7 : -15}
          >
            <Icon src="dragIcon" className={styles.dragIcon} />
          </FloatingContainer>
        )}

        <Slider
          range
          defaultValue={[0, 1]}
          value={handlersPositions}
          min={0}
          max={1}
          tabIndex={[0, 1]}
          step={1 / (trimmingVideoDuration * 10)}
          allowCross={false}
          onChange={updateTrimmingValues}
          className={styles.range}
          trackStyle={[
            {
              backgroundColor: 'white',
              height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
              borderRadius: '0',
              border: 'none',
              borderTop: '1px solid #7C43AA',
              borderBottom: '1px solid #7C43AA',
            },
          ]}
          handleStyle={[
            {
              width: '12px',
              height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
              backgroundColor: chosenHandler === 0 ? '#7C43AA' : '#BDBDBD',
              backgroundImage: `url(${controlBgImage})`,
              backgroundPosition: '50% 50%',
              backgroundRepeat: 'no-repeat',
              borderRadius: '0',
              borderTopLeftRadius: '4px',
              borderBottomLeftRadius: '4px',
              border: 'none',
              borderTop: '1px solid #7C43AA',
              borderLeft: '1px solid #7C43AA',
              borderBottom: '1px solid #7C43AA',
              boxShadow: 'none',
              outline: 'none',
              position: 'absolute',
              marginLeft: '-12px',
              zIndex: '51',
              top: '10px',
            },
            {
              width: '12px',
              height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
              backgroundColor: chosenHandler === 1 ? '#7C43AA' : '#BDBDBD',
              backgroundImage: `url(${controlBgImage})`,
              backgroundPosition: '50% 50%',
              backgroundRepeat: 'no-repeat',
              borderRadius: '0',
              borderTopRightRadius: '4px',
              borderBottomRightRadius: '4px',
              border: 'none',
              borderTop: '1px solid #7C43AA',
              borderRight: '1px solid #7C43AA',
              borderBottom: '1px solid #7C43AA',
              boxShadow: 'none',
              outline: 'none',
              position: 'absolute',
              top: '10px',
              right: '0',
              marginLeft: '-3px',
              zIndex: '51',
            },
          ]}
          railStyle={{
            height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
            border: '1px solid #BDBDBD',
            background: '#EEEEEE',
            borderRadius: '4px',
            boxShadow: 'none',
          }}
          onAfterChange={() => {
            setActionFinished(true);
          }}
        />
        <Slider
          className={styles.videoProgress}
          trackStyle={{
            backgroundColor: 'rgba(124, 67, 170, 0.1)',
            background: `linear-gradient(to right,  transparent 0%, transparent ${(startTimeInput /
              trimmingVideoDuration /
              currentProgress) *
              100}%,  rgba(124, 67, 170, 0.1) ${(startTimeInput / trimmingVideoDuration / currentProgress) * 100}% ${currentProgress *
              100}%)`,
            height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
            top: '30%',
            border: '0px',
          }}
          handleStyle={{
            backgroundImage: `url(${position})`,
            backgroundPosition: '50% 0%',
            borderRadius: '0',
            border: 'none',
            boxShadow: 'none',
            outline: 'none',
            cursor: 'default',
            backgroundRepeat: 'no-repeat',
            backgroundColor: 'transparent',
            zIndex: '50',
            height: `${HANDLER_NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
            top: `${HANDLER_NORMAN_TOP * (min ? MIN_HEIGHT_COEF : 1)}px`,
          }}
          railStyle={{
            height: `${RAIL_NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
            background: 'transparent',
            zIndex: '50',
            boxShadow: 'none',
          }}
          min={0}
          max={1}
          step={0.001}
          onBeforeChange={onSeekMouseDown}
          onChange={onSeekChange}
          onAfterChange={onSeekMouseUp}
          value={currentProgress}
          included
        />
        {waveform ? (
          <div className={cx(styles.waveformContainer, { [styles.min]: min })}>
            <Waveform
              waveformData={waveform.map(item => item * 10000)}
              parentRef={trimContainerRef}
              trimStartPos={startTimeInput / trimmingVideoDuration}
              progress={currentProgress}
              min={min}
            />
          </div>
        ) : (
          <Spinner
            width={`${min ? 22 : 30}px`}
            height={`${min ? 22 : 30}px`}
            size={`${min ? 2 : 3}px`}
            className={cx(styles.spinnerIcon, { [styles.min]: min })}
          />
        )}
        <FloatingContainer
          parentRef={trimContainerRef}
          floatingValue={currentProgress}
          correction={8}
          className={cx(styles.progressContainer, { [styles.min]: min })}
        >
          <TimeInput
            inputClassName={cx(styles.timeInput, { [styles.min]: min })}
            disabled={true}
            value={currentProgress * trimmingVideoDuration}
            variant="basic"
            showControls={false}
          />
        </FloatingContainer>
        {chosenHandler >= 0 && (
          <FloatingContainer
            className={cx(styles.trimmingContainer)}
            parentRef={trimContainerRef}
            floatingValue={handlersPositions[chosenHandler]}
          >
            {chosenHandler === 0 ? (
              <TimeInput
                inputClassName={cx(styles.timeInput, { [styles.min]: min })}
                disabled={true}
                value={startTimeInput}
                max={endTimeInput - MINIMUM_TRIMMING_DURATION < 0 ? 0 : endTimeInput - MINIMUM_TRIMMING_DURATION}
                variant="basic"
                showControls={false}
              />
            ) : (
              <TimeInput
                className={styles.timeInput}
                disabled={true}
                value={endTimeInput}
                min={
                  startTimeInput + MINIMUM_TRIMMING_DURATION > trimmingVideoDuration
                    ? startTimeInput
                    : startTimeInput + MINIMUM_TRIMMING_DURATION
                }
                max={trimmingVideoDuration}
                variant="basic"
                showControls={false}
              />
            )}
            <div className={styles.keyHint}>
              <span>{t('my_video.tools.trim.use')}</span>
              <Icon src="keyArrowLeft" className={styles.keyIcon} />
              <Icon src="keyArrowRight" className={styles.keyIcon} />
            </div>
          </FloatingContainer>
        )}
      </div>
    </div>
  );
};

export default VideoTrimmingControls;
